import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import styles from './CameraCaptureModal.module.scss';

const CameraCaptureModal = ({ cameraModalOpen, setCameraModalOpen, setImageData, imageData, video = false }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const [image, setImage] = useState(null);
    const [recording, setRecording] = useState(false);
    const [recordedChunks, setRecordedChunks] = useState([]);

    useEffect(() => {
        let stream;
        const getVideo = async () => {
            try {
                stream = await navigator.mediaDevices.getUserMedia({ video: true });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            } catch (err) {
                console.error('Error accessing the camera: ', err);
            }
        };

        if (cameraModalOpen) {
            getVideo();
        }

        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [cameraModalOpen, image]);

    const takePhoto = () => {
        const width = 310;
        const height = 240;
        const canvas = canvasRef.current;
        const video = videoRef.current;

        if (canvas && video) {
            const context = canvas.getContext('2d');
            canvas.width = width;
            canvas.height = height;
            context.drawImage(video, 0, 0, width, height);

            const imageDataUrl = canvas.toDataURL('image/png');
            setImage(imageDataUrl);
        }
    };

    const startRecording = () => {
        const stream = videoRef.current.srcObject;
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                setRecordedChunks((prev) => [...prev, event.data]);
            }
        };

        mediaRecorder.start();
        setRecording(true);
    };

    const stopRecording = () => {
        mediaRecorderRef.current.stop();
        setRecording(false);
    };

    const submitVideo = () => {
        const videoBlob = new Blob(recordedChunks, { type: 'video/webm' });
        const videoUrl = URL.createObjectURL(videoBlob);
        setImageData([...imageData, videoUrl]);
        setCameraModalOpen(false);
    };

    const clearPhoto = () => {
        setImage(null);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
    };

    const submitPhoto = () => {
        setImageData([...imageData, image]);
        setCameraModalOpen(false);
    };

    return (
        <Modal
            isOpen={cameraModalOpen}
            onRequestClose={() => setCameraModalOpen(false)}
            contentLabel="Camera Modal"
            className={styles.modal}
            overlayClassName={styles.overlay}
        >
            <div className={styles.mainContainer}>
                {video ? (
                    <div className={styles.cameraContainer}>
                        <video ref={videoRef} autoPlay playsInline className={styles.videoContainer} />
                        <div className={styles.buttonContainer}>
                            {!recording ? (
                                <button onClick={startRecording}>Start Recording</button>
                            ) : (
                                <button onClick={stopRecording}>Stop Recording</button>
                            )}
                            {recordedChunks.length > 0 && (
                                <button onClick={submitVideo}>Submit Video</button>
                            )}
                            <button onClick={() => setCameraModalOpen(false)}>Back</button>
                        </div>
                    </div>
                ) : image ? (
                    <div className={styles.displayedPic}>
                        <img src={image} alt="Captured" className={styles.picture} />
                        <div className={styles.buttonContainer}>
                            <button onClick={clearPhoto}>Clear Photo</button>
                            <button onClick={submitPhoto}>Submit Photo</button>
                        </div>
                    </div>
                ) : (
                    <div className={styles.cameraContainer}>
                        <video ref={videoRef} autoPlay playsInline className={styles.videoContainer} />
                        <div className={styles.buttonContainer}>
                            <button onClick={() => setCameraModalOpen(false)}>Back</button>
                            <button onClick={takePhoto}>Take Photo</button>
                        </div>
                    </div>
                )}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
        </Modal>
    );
};

export default React.memo(CameraCaptureModal);
